// frontend/src/components/crm/TeamManagement.jsx
import React, { useState, useEffect } from 'react';
import { useCRM } from '../../contexts/CRMContext';
import { useBranding } from '../../contexts/BrandingContext';
import { useDialog } from '../../components/kibo-ui/dialog';
import { PERMISSIONS, hasPermission } from '../../utils/permissions';
import crmApi, { agencyAPI } from '../../utils/crmApi';
import {
  UserPlusIcon,
  TrashIcon,
  ShieldCheckIcon,
  UserIcon,
  EnvelopeIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  EyeIcon,
  PencilIcon,
  KeyIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

const TeamManagement = () => {
  const { user, currentAgency } = useCRM();
  const { branding } = useBranding();
  const { openDialog } = useDialog();
  
  const [teamMembers, setTeamMembers] = useState([]);
  const [subscription, setSubscription] = useState(null); // legacy shape from old endpoint
  const [seatInfo, setSeatInfo] = useState({ used: 0, max: 0, available: 0, planName: 'trial' });
  const [loading, setLoading] = useState(true);
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  const [inviteForm, setInviteForm] = useState({
    email: '',
    name: '',
    role: 'agency_user',
    permissions: {
      canManageAccounts: false,
      canManageClients: false,
      canViewPII: false,
      canEditTasks: false,
      canSendEmail: false,
      canManageEmailTemplates: false,
      canExportData: false,
      canViewReports: false,
      canManageRoles: false,
      canManageUsers: false,
      canManageAgency: false,
      canManageContent: false,
    }
  });
  const [editingMember, setEditingMember] = useState(null);

  // Role definitions
  const roles = [
    {
      id: 'agency_admin',
      name: 'Agency Admin',
      description: 'Full access to all agency features and settings',
      icon: ShieldCheckIcon,
      color: 'text-purple-600',
      bgColor: 'bg-purple-100'
    },
    {
      id: 'agency_user',
      name: 'Agency User',
      description: 'Can manage clients, accounts, and tasks',
      icon: UserIcon,
      color: 'text-blue-600',
      bgColor: 'bg-blue-100'
    },
    {
      id: 'account_contact',
      name: 'Account Contact',
      description: 'Limited access to specific account portal',
      icon: EyeIcon,
      color: 'text-green-600',
      bgColor: 'bg-green-100'
    },
    {
      id: 'client',
      name: 'Client',
      description: 'Access to client portal only',
      icon: UserIcon,
      color: 'text-muted-foreground',
      bgColor: 'bg-muted'
    }
  ];

  // Permission definitions
  const permissionGroups = [
    {
      name: 'Account & Client Management',
      permissions: [
        { key: 'canManageAccounts', label: 'Manage Accounts', description: 'Create, edit, and delete accounts' },
        { key: 'canManageClients', label: 'Manage Clients', description: 'Create, edit, and delete clients' },
        { key: 'canViewPII', label: 'View PII', description: 'Access personally identifiable information' },
      ]
    },
    {
      name: 'Task Management',
      permissions: [
        { key: 'canEditTasks', label: 'Edit Tasks', description: 'Create and modify tasks' },
      ]
    },
    {
      name: 'Email & Communications',
      permissions: [
        { key: 'canSendEmail', label: 'Send Emails', description: 'Send emails to clients and contacts' },
        { key: 'canManageEmailTemplates', label: 'Manage Email Templates', description: 'Create and edit email templates' },
      ]
    },
    {
      name: 'Data & Analytics',
      permissions: [
        { key: 'canExportData', label: 'Export Data', description: 'Export data and generate reports' },
        { key: 'canViewReports', label: 'View Reports', description: 'Access analytics and reports' },
      ]
    },
    {
      name: 'User & Role Management',
      permissions: [
        { key: 'canManageRoles', label: 'Manage Roles', description: 'Create and modify user roles' },
        { key: 'canManageUsers', label: 'Manage Users', description: 'Invite, edit, and remove users' },
      ]
    },
    {
      name: 'Agency Management',
      permissions: [
        { key: 'canManageAgency', label: 'Manage Agency', description: 'Edit agency settings and branding' },
        { key: 'canManageContent', label: 'Manage Content', description: 'Manage documents and content' },
      ]
    }
  ];

  // Load team members and seat/billing info
  useEffect(() => {
    loadData();
  }, [currentAgency]);

  const loadData = async () => {
    if (!currentAgency?._id) return;
    
    try {
      setLoading(true);
      
      // Load members and billing summary in parallel using crmApi
      const [membersRes, billingRes] = await Promise.all([
        agencyAPI.getMembers(currentAgency._id),
        crmApi.get(`/agencies/${currentAgency._id}/billing`)
      ]);
      
      let membersList = [];
      if (membersRes.data?.success && Array.isArray(membersRes.data.members)) {
        membersList = membersRes.data.members;
        setTeamMembers(membersList);
      }
      
      if (billingRes.data?.success) {
        const billing = billingRes.data.data || {};
        // Count only staff seats (exclude portal-only roles like client/account_contact)
        const used = membersList.filter(m => m.status === 'active' && (m.role === 'agency_admin' || m.role === 'agency_user')).length;
        // Use seatLimit if set, otherwise fallback to featureFlags.maxSeats, otherwise plan defaults
        // Priority: seatLimit > featureFlags.maxSeats > plan defaults > 0
        let max = 0;
        if (typeof billing.seatLimit === 'number' && billing.seatLimit !== null && billing.seatLimit !== undefined) {
          max = billing.seatLimit;
        } else if (typeof billing.featureFlags?.maxSeats === 'number' && billing.featureFlags.maxSeats !== null && billing.featureFlags.maxSeats !== undefined) {
          max = billing.featureFlags.maxSeats;
        } else {
          // Last resort: use plan defaults based on planTier
          const planTier = billing.planTier || 'trial';
          const planDefaults = {
            trial: 3,
            starter: 3,
            professional: 10,
            enterprise: -1 // unlimited
          };
          max = planDefaults[planTier] || 0;
        }
        const available = max === -1 ? 'unlimited' : Math.max(0, max - used);
        setSeatInfo({ used, max, available, planName: billing.planTier || 'trial' });
        
      } else {
        // Billing endpoint failed - use fallback
        const used = membersList.filter(m => m.status === 'active' && (m.role === 'agency_admin' || m.role === 'agency_user')).length;
        // Use plan defaults as fallback
        const planTier = currentAgency?.planTier || 'trial';
        const planDefaults = {
          trial: 3,
          starter: 3,
          professional: 10,
          enterprise: -1
        };
        const max = planDefaults[planTier] || 0;
        const available = max === -1 ? 'unlimited' : Math.max(0, max - used);
        setSeatInfo({ used, max, available, planName: planTier });
      }
    } catch (error) {
      console.error('Failed to load data:', error);
      // On error, still try to set seat info from members list
      const used = teamMembers.filter(m => m.status === 'active' && (m.role === 'agency_admin' || m.role === 'agency_user')).length;
      const planTier = currentAgency?.planTier || 'trial';
      const planDefaults = {
        trial: 3,
        starter: 3,
        professional: 10,
        enterprise: -1
      };
      const max = planDefaults[planTier] || 0;
      const available = max === -1 ? 'unlimited' : Math.max(0, max - used);
      setSeatInfo({ used, max, available, planName: planTier });
    } finally {
      setLoading(false);
    }
  };

  const handleInviteUser = async (e) => {
    e.preventDefault();
    
    try {
      const response = await crmApi.post(`/agencies/${currentAgency._id}/invite`, inviteForm);
      const data = response.data;
      
      if (data.success) {
        toast.success('Invitation sent successfully!');
        setShowInviteForm(false);
        setInviteForm({
          email: '',
          name: '',
          role: 'agency_user',
          permissions: {}
        });
        loadData();
      } else if (data.recommendedPlan) {
        // Show upgrade prompt
        setShowUpgradePrompt(true);
        toast.error(data.message || 'Please upgrade your plan to continue');
      } else {
        toast.error(data.message || 'Failed to send invitation');
      }
    } catch (error) {
      console.error('Failed to invite user:', error);
      const errorData = error.response?.data;
      if (error.response?.status === 403 && errorData?.recommendedPlan) {
        setShowUpgradePrompt(true);
        toast.error(errorData.message || 'Please upgrade your plan to continue');
      } else {
        toast.error(errorData?.message || 'Failed to send invitation');
      }
    }
  };

  const handleRemoveUser = (member) => {
    openDialog({
      title: 'Remove Team Member',
      description: `Are you sure you want to remove ${member.name} from the team? They will lose access to all agency resources.`,
      confirmText: 'Remove',
      cancelText: 'Cancel',
      variant: 'danger',
      onConfirm: async () => {
        try {
          const response = await crmApi.delete(`/agencies/${currentAgency._id}/members/${member._id}`);
          const data = response.data;
          
          if (data.success) {
            toast.success(`${member.name} removed from team`);
            loadData();
          } else {
            toast.error(data.message || 'Failed to remove team member');
          }
        } catch (error) {
          console.error('Failed to remove team member:', error);
          toast.error(error.response?.data?.message || 'Failed to remove team member');
        }
      }
    });
  };

  const handleToggleAdmin = (member) => {
    const action = member.role === 'agency_admin' ? 'revoke admin rights from' : 'delegate admin rights to';
    const newRole = member.role === 'agency_admin' ? 'agency_user' : 'agency_admin';
    
    openDialog({
      title: member.role === 'agency_admin' ? 'Revoke Admin Rights' : 'Delegate Admin Rights',
      description: `Are you sure you want to ${action} ${member.name}?`,
      confirmText: member.role === 'agency_admin' ? 'Revoke' : 'Delegate',
      cancelText: 'Cancel',
      variant: member.role === 'agency_admin' ? 'danger' : 'default',
      onConfirm: async () => {
        try {
          const response = await crmApi.patch(`/agencies/${currentAgency._id}/members/${member._id}`, { role: newRole });
          const data = response.data;
          
          if (data.success) {
            toast.success(`${member.name} is now ${newRole === 'agency_admin' ? 'an admin' : 'a regular user'}`);
            loadData();
          } else {
            toast.error(data.message || 'Failed to update user role');
          }
        } catch (error) {
          console.error('Failed to update user role:', error);
          toast.error(error.response?.data?.message || 'Failed to update user role');
        }
      }
    });
  };

  const handleUpdatePermissions = async (member) => {
    setEditingMember(member);
  };

  const savePermissions = async () => {
    if (!editingMember) return;
    
    try {
      const response = await crmApi.patch(`/agencies/${currentAgency._id}/members/${editingMember._id}`, { permissions: editingMember.permissions });
      const data = response.data;
      
      if (data.success) {
        toast.success('Permissions updated successfully');
        setEditingMember(null);
        loadData();
      } else {
        toast.error(data.message || 'Failed to update permissions');
      }
    } catch (error) {
      console.error('Failed to update permissions:', error);
      toast.error('Failed to update permissions');
    }
  };

  const getRoleInfo = (roleId) => {
    return roles.find(r => r.id === roleId) || roles[1];
  };

  const getStatusBadge = (status) => {
    const config = {
      active: { icon: CheckCircleIcon, color: branding.secondaryColor, bg: `${branding.secondaryColor}20`, text: 'Active' },
      pending: { icon: ClockIcon, color: branding.accentColor, bg: `${branding.accentColor}20`, text: 'Pending' },
      suspended: { icon: XCircleIcon, color: '#EF4444', bg: '#FEE2E2', text: 'Suspended' }
    };
    return config[status] || config.pending;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: branding.primaryColor }}></div>
      </div>
    );
  }

  // Filter roles based on subscription plan
  const availableRoles = ['agency_admin', 'agency_user'];
  const filteredRoles = roles.filter(role => availableRoles.includes(role.id));

  const staffMembers = (teamMembers || []).filter(m => m.role === 'agency_admin' || m.role === 'agency_user');

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-foreground">Team Management</h2>
          <p className="text-muted-foreground mt-1">
            Invite team members, manage roles, and configure permissions
          </p>
        </div>
        <button
          onClick={() => setShowInviteForm(!showInviteForm)}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2"
          style={{ backgroundColor: branding.primaryColor, '--tw-ring-color': branding.primaryColor }}
        >
          <UserPlusIcon className="h-5 w-5 mr-2" />
          Invite Member
        </button>
      </div>

      {/* Subscription Info */}
      {seatInfo && (
        <div className="bg-gradient-to-r from-blue-50 dark:from-blue-950/10 to-indigo-50 dark:to-indigo-950/10 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center space-x-3">
                <h3 className="text-sm font-semibold text-foreground">{String(seatInfo.planName).charAt(0).toUpperCase() + String(seatInfo.planName).slice(1)} Plan</h3>
              </div>
              <div className="mt-2 flex items-center space-x-6 text-sm text-muted-foreground">
                <div>
                  <span className="font-medium" style={{ color: branding.primaryColor }}>
                    {seatInfo.used}/{(currentAgency?.featureFlags?.maxSeats === -1 || seatInfo.max === -1) ? 'Unlimited' : seatInfo.max}
                  </span>
                  <span className="ml-1">seats</span>
                </div>
                <div className="text-muted-foreground">•</div>
                <div>
                  <span className="font-medium">{availableRoles.length}</span>
                  <span className="ml-1">role types available</span>
                </div>
              </div>
              {(seatInfo.available !== 'unlimited' && seatInfo.available <= 2 && currentAgency?.featureFlags?.maxSeats !== -1) && (
                <p className="mt-2 text-xs" style={{ color: branding.accentColor }}>
                  ⚠️ Only {seatInfo.available} seat{seatInfo.available !== 1 ? 's' : ''} remaining
                </p>
              )}
            </div>
              <button
              onClick={() => {
                // navigate to Billing tab in Agency Settings
                const el = document.querySelector('[data-agency-settings-billing-tab]');
                if (el) el.click();
              }}
                className="ml-4 px-4 py-2 text-sm font-medium rounded-lg border-2 transition-colors"
                style={{ 
                  borderColor: branding.primaryColor,
                  color: branding.primaryColor,
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = branding.primaryColor;
                  e.target.style.color = 'white';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.color = branding.primaryColor;
                }}
              >
              Manage seats
              </button>
          </div>
        </div>
      )}

      {/* Invite Form */}
      {showInviteForm && (
        <div className="bg-muted border border-input focus:ring-2 focus:ring-ring focus:border-ring rounded-lg p-6">
          <h3 className="text-lg font-medium text-foreground mb-4">Invite New Team Member</h3>
          <form onSubmit={handleInviteUser} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Name</label>
                <input
                  type="text"
                  required
                  value={inviteForm.name}
                  onChange={(e) => setInviteForm({ ...inviteForm, name: e.target.value })}
                  className="w-full px-4 py-2 border border-input rounded-lg focus:ring-2  focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring"
                  style={{ '--tw-ring-color': branding.primaryColor }}
                  placeholder="John Doe"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Email</label>
                <input
                  type="email"
                  required
                  value={inviteForm.email}
                  onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                  className="w-full px-4 py-2 border border-input rounded-lg focus:ring-2  focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring"
                  style={{ '--tw-ring-color': branding.primaryColor }}
                  placeholder="john@example.com"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Role</label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {filteredRoles.map((role) => {
                  const Icon = role.icon;
                  return (
                    <label
                      key={role.id}
                      className={`relative flex items-start p-4 border-2 rounded-lg cursor-pointer hover:bg-muted transition-colors ${
                        inviteForm.role === role.id ? 'border-transparent' : 'border-input'
                      }`}
                      style={inviteForm.role === role.id ? { borderColor: branding.primaryColor, backgroundColor: `${branding.primaryColor}10` } : {}}
                    >
                      <input
                        type="radio"
                        name="role"
                        value={role.id}
                        checked={inviteForm.role === role.id}
                        onChange={(e) => setInviteForm({ ...inviteForm, role: e.target.value })}
                        className="sr-only"
                      />
                      <div className={`flex-shrink-0 flex items-center justify-center h-10 w-10 rounded-lg ${role.bgColor}`}>
                        <Icon className={`h-6 w-6 ${role.color}`} />
                      </div>
                      <div className="ml-3 flex-1">
                        <span className="block text-sm font-medium text-foreground">{role.name}</span>
                        <span className="block text-xs text-muted-foreground mt-1">{role.description}</span>
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>

            {/* Custom Permissions (only for agency_user) */}
            {inviteForm.role === 'agency_user' && (
              <div>
                <label className="block text-sm font-medium text-foreground mb-3">Custom Permissions</label>
                <div className="space-y-4">
                  {permissionGroups.map((group) => (
                    <div key={group.name} className="border border-input focus:ring-2 focus:ring-ring focus:border-ring rounded-lg p-4">
                      <h4 className="text-sm font-medium text-foreground mb-3">{group.name}</h4>
                      <div className="space-y-2">
                        {group.permissions.map((perm) => (
                          <label key={perm.key} className="flex items-start">
                            <input
                              type="checkbox"
                              checked={inviteForm.permissions[perm.key] || false}
                              onChange={(e) => setInviteForm({
                                ...inviteForm,
                                permissions: { ...inviteForm.permissions, [perm.key]: e.target.checked }
                              })}
                              className="mt-1 h-4 w-4 rounded border-input focus:ring-2"
                              style={{ '--tw-ring-color': branding.primaryColor, accentColor: branding.primaryColor }}
                            />
                            <div className="ml-3">
                              <span className="block text-sm font-medium text-foreground">{perm.label}</span>
                              <span className="block text-xs text-muted-foreground">{perm.description}</span>
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex items-center space-x-3 pt-4">
              <button
                type="submit"
                className="px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white hover:opacity-90"
                style={{ backgroundColor: branding.primaryColor }}
              >
                Send Invitation
              </button>
              <button
                type="button"
                onClick={() => setShowInviteForm(false)}
                className="px-4 py-2 border border-input focus:ring-2 focus:ring-ring focus:border-ring rounded-lg text-sm font-medium text-foreground hover:bg-muted"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Team Members List */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-foreground">
          Team Members ({staffMembers.length})
        </h3>
        
        <div className="bg-card border border-border rounded-lg divide-y divide-border">
          {staffMembers.map((member) => {
            const roleInfo = getRoleInfo(member.role);
            const statusBadge = getStatusBadge(member.status);
            const StatusIcon = statusBadge.icon;
            const RoleIcon = roleInfo.icon;

            return (
              <div key={member._id} className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-4">
                    {/* Avatar */}
                    <div
                      className="flex-shrink-0 h-12 w-12 rounded-full flex items-center justify-center text-white font-bold text-lg"
                      style={{ backgroundColor: branding.primaryColor }}
                    >
                      {member.name.charAt(0).toUpperCase()}
                    </div>

                    {/* User Info */}
                    <div className="flex-1">
                      <div className="flex items-center space-x-2">
                        <h4 className="text-base font-medium text-foreground">{member.name}</h4>
                        {member.isCurrentUser && (
                          <span className="px-2 py-0.5 text-xs font-medium rounded-full" style={{ backgroundColor: `${branding.primaryColor}20`, color: branding.primaryColor }}>
                            You
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground mt-0.5">{member.email}</p>
                      
                      {/* Role & Status */}
                      <div className="flex items-center space-x-3 mt-2">
                        <div className="flex items-center space-x-1.5">
                          <RoleIcon className={`h-4 w-4 ${roleInfo.color}`} />
                          <span className="text-sm font-medium text-foreground">{roleInfo.name}</span>
                        </div>
                        <span className="text-muted-foreground">•</span>
                        <div className="flex items-center space-x-1.5">
                          <StatusIcon className="h-4 w-4" style={{ color: statusBadge.color }} />
                          <span className="text-sm" style={{ color: statusBadge.color }}>{statusBadge.text}</span>
                        </div>
                        <span className="text-muted-foreground">•</span>
                        <span className="text-sm text-muted-foreground">
                          Joined {new Date(member.joinedAt).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  {!member.isCurrentUser && (
                    <div className="flex items-center space-x-2">
                      {hasPermission(user, PERMISSIONS.MANAGE_ROLES, currentAgency?._id) && (
                        <button
                          onClick={() => handleUpdatePermissions(member)}
                          className="p-2 text-muted-foreground hover:text-muted-foreground rounded-lg hover:bg-muted"
                          title="Edit Permissions"
                        >
                          <KeyIcon className="h-5 w-5" />
                        </button>
                      )}
                      {hasPermission(user, PERMISSIONS.MANAGE_ROLES, currentAgency?._id) && (
                        <button
                          onClick={() => handleToggleAdmin(member)}
                          className="p-2 rounded-lg hover:bg-muted"
                          style={{ color: member.role === 'agency_admin' ? branding.accentColor : branding.secondaryColor }}
                          title={member.role === 'agency_admin' ? 'Revoke Admin' : 'Make Admin'}
                        >
                          <ShieldCheckIcon className="h-5 w-5" />
                        </button>
                      )}
                      {hasPermission(user, PERMISSIONS.REMOVE_USERS, currentAgency?._id) && (
                        <button
                          onClick={() => handleRemoveUser(member)}
                          className="p-2 text-red-400 hover:text-red-600 rounded-lg hover:bg-red-50"
                          title="Remove User"
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Permissions Editing Modal */}
      {editingMember && (
        <div className="fixed inset-0 z-[9999] overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            {/* Background overlay */}
            <div 
              className="fixed inset-0 transition-opacity bg-black/50 dark:bg-black/70"
              onClick={() => setEditingMember(null)}
            ></div>

            {/* Modal panel */}
            <div className="inline-block align-bottom bg-card rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
              <div className="bg-card px-6 pt-5 pb-4">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-medium text-foreground">
                      Edit Permissions for {editingMember.name}
                    </h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      Configure what {editingMember.name} can do in this agency
                    </p>
                  </div>
                  <button
                    onClick={() => setEditingMember(null)}
                    className="text-muted-foreground hover:text-muted-foreground"
                  >
                    <XCircleIcon className="h-6 w-6" />
                  </button>
                </div>

                <div className="mt-6 space-y-6 max-h-96 overflow-y-auto scrollbar-modal">
                  {permissionGroups.map((group) => (
                    <div key={group.name} className="border border-input focus:ring-2 focus:ring-ring focus:border-ring rounded-lg p-4">
                      <h4 className="text-sm font-medium text-foreground mb-3">{group.name}</h4>
                      <div className="space-y-3">
                        {group.permissions.map((perm) => (
                          <label key={perm.key} className="flex items-start cursor-pointer">
                            <input
                              type="checkbox"
                              checked={editingMember.permissions?.[perm.key] || false}
                              onChange={(e) => setEditingMember({
                                ...editingMember,
                                permissions: {
                                  ...editingMember.permissions,
                                  [perm.key]: e.target.checked
                                }
                              })}
                              className="mt-1 h-4 w-4 rounded border-input focus:ring-2"
                              style={{ '--tw-ring-color': branding.primaryColor, accentColor: branding.primaryColor }}
                            />
                            <div className="ml-3">
                              <span className="block text-sm font-medium text-foreground">{perm.label}</span>
                              <span className="block text-xs text-muted-foreground">{perm.description}</span>
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-muted px-6 py-4 flex items-center justify-end space-x-3">
                <button
                  onClick={() => setEditingMember(null)}
                  className="px-4 py-2 border border-input focus:ring-2 focus:ring-ring focus:border-ring rounded-lg text-sm font-medium text-foreground hover:bg-muted"
                >
                  Cancel
                </button>
                <button
                  onClick={savePermissions}
                  className="px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white hover:opacity-90"
                  style={{ backgroundColor: branding.primaryColor }}
                >
                  Save Permissions
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamManagement;

